import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Clock, FileText, MessageSquare, Hash, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearchQuery, useRecentlyViewedQuery } from 'service/project';
import { GlobalSearchResult, GlobalSearchGroup } from 'types/globalSearch';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import { workTypeUIMap } from 'components/modules/ProjectManagement/Dropdowns/TaskTypeDropdown';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Converts "**word**" markers in a snippet into JSX with highlighted spans */
const renderSnippet = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <mark
          key={i}
          className="bg-yellow-400/25 text-yellow-200 rounded px-0.5 not-italic font-semibold"
        >
          {part.slice(2, -2)}
        </mark>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

/** Use a debounced value */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ResultRowProps {
  result: GlobalSearchResult;
  isHighlighted: boolean;
  onSelect: (result: GlobalSearchResult) => void;
  onMouseEnter: () => void;
}

const ResultRow: React.FC<ResultRowProps> = ({ result, isHighlighted, onSelect, onMouseEnter }) => {
  const rowRef = useRef<HTMLButtonElement>(null);
  const snippet = result.descriptionSnippet || result.commentSnippet;
  const isComment = !!result.commentSnippet && !result.descriptionSnippet;
  const isDescription = !!result.descriptionSnippet;

  const workTypeUI = result.workTypeCategory 
    ? (workTypeUIMap as Record<string, { icon: React.ReactNode; color: string } | undefined>)[result.workTypeCategory]
    : undefined;

  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isHighlighted]);

  return (
    <button
      ref={rowRef}
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={() => onSelect(result)}
      className={`w-full text-left px-4 py-3 flex flex-col gap-1 transition-colors group ${
        isHighlighted ? 'bg-[#2d3142]/80' : 'hover:bg-[#2d3142]/50'
      }`}
    >
      {/* Top row: key + title */}
      <div className="flex items-center gap-2 min-w-0">
        {workTypeUI && (
          <span className={`shrink-0 flex items-center ${workTypeUI.color}`}>
            {workTypeUI.icon}
          </span>
        )}
        <span className="text-[11px] font-bold text-[#8b5cf6] shrink-0 tabular-nums">
          {result.workItemKey}
        </span>
        <span className="text-sm text-white truncate flex-1">{result.title}</span>
        {result.matchType === 'DESCRIPTION' && (
          <span className="shrink-0 flex items-center gap-1 text-[10px] text-[#8e94a5] bg-[#1c2030] border border-[#2d3142] rounded px-1.5 py-0.5">
            <FileText className="w-3 h-3" />
            Desc
          </span>
        )}
        {result.matchType === 'COMMENT' && (
          <span className="shrink-0 flex items-center gap-1 text-[10px] text-[#8e94a5] bg-[#1c2030] border border-[#2d3142] rounded px-1.5 py-0.5">
            <MessageSquare className="w-3 h-3" />
            Comment
          </span>
        )}
      </div>

      {/* Snippet row */}
      {snippet && (
        <p className="text-xs text-[#8e94a5] leading-relaxed line-clamp-2 pl-0 italic">
          {isComment ? (
            <>
              <MessageSquare className="w-3 h-3 inline mr-1 text-[#8b5cf6] not-italic" />
              {renderSnippet(snippet)}
            </>
          ) : (
            <>
              <FileText className="w-3 h-3 inline mr-1 text-[#8b5cf6] not-italic" />
              {renderSnippet(snippet)}
            </>
          )}
        </p>
      )}

      {/* Project name */}
      <p className="text-[10px] text-[#5c6175] truncate">{result.projectName}</p>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface GlobalSearchBarProps {
  className?: string;
  placeholder?: string;
  projectUuid?: string;
  disableKeyboardShortcut?: boolean;
  inlineMode?: boolean;
}

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({ className, placeholder = "Search items...", projectUuid, disableKeyboardShortcut = false, inlineMode = false }) => {
  const navigate = useNavigate();
  const authUser = useAppSelector(selectAuthUser);
  const orgSlug = authUser?.orgSlug ?? 'org';

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Queries
  const { data: searchData, isFetching: isSearching } = useGlobalSearchQuery(debouncedQuery, projectUuid);
  const { data: recentData, isFetching: isRecentFetching } = useRecentlyViewedQuery(
    isOpen && query.trim().length === 0,
    projectUuid
  );

  // Flatten all results in display order for keyboard nav
  const allResults: GlobalSearchResult[] = React.useMemo(() => {
    if (debouncedQuery.trim().length === 0) {
      return recentData?.recentlyViewed ?? [];
    }
    const groups = searchData?.groups ?? [];
    return groups.flatMap((g) => g.results);
  }, [debouncedQuery, searchData, recentData]);

  const openOverlay = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const closeOverlay = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
  }, []);

  // Lock body scroll when modal is open (only in modal mode)
  useEffect(() => {
    if (isOpen && !inlineMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, inlineMode]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
        closeOverlay();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closeOverlay]);

  // Keyboard shortcut: Cmd/Ctrl+K opens search
  useEffect(() => {
    if (disableKeyboardShortcut) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        isOpen ? closeOverlay() : openOverlay();
      }
      if (e.key === 'Escape' && isOpen) closeOverlay();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, openOverlay, closeOverlay, disableKeyboardShortcut]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && allResults[highlightedIndex]) {
        handleSelect(allResults[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      closeOverlay();
    }
  };

  const handleSelect = (result: GlobalSearchResult) => {
    const base = `/${orgSlug}/pmo/${result.projectKey}/${result.workItemKey}`;
    const hash =
      result.matchType === 'COMMENT' && result.commentUuid
        ? result.rootCommentUuid
          ? `#comment-${result.commentUuid}_root-${result.rootCommentUuid}`
          : `#comment-${result.commentUuid}`
        : '';
    navigate(`${base}${hash}`);
    closeOverlay();
  };

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [debouncedQuery]);

  const showRecent = isOpen && query.trim().length === 0;
  const showResults = isOpen && debouncedQuery.trim().length > 0;
  const groups: GlobalSearchGroup[] = (searchData?.groups ?? []).filter((g) => g.totalCount > 0);
  const recentItems: GlobalSearchResult[] = recentData?.recentlyViewed ?? [];
  const isLoading = isSearching || isRecentFetching;
  const hasNoResults =
    showResults && !isLoading && debouncedQuery === query && groups.length === 0;

  // Accumulate flat index offset per group for keyboard highlight mapping
  let flatIndex = 0;

  const renderInputRow = () => (
    <div className={`flex items-center gap-3 px-4 ${inlineMode ? 'h-full w-full' : 'py-3 border-b border-[#2d3142]/60'}`}>
      {isLoading ? (
        <svg
          className="animate-spin w-4 h-4 text-[#8b5cf6] shrink-0"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <Search className="w-4 h-4 text-[#5c6175] shrink-0" />
      )}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onFocus={() => { if (inlineMode) setIsOpen(true); }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (inlineMode) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={inlineMode ? placeholder : "Search by title, key, description or comment..."}
        className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-[#5c6175] w-full"
        autoComplete="off"
      />
      {query.length > 0 && (
        <button
          type="button"
          onClick={() => { setQuery(''); inputRef.current?.focus(); }}
          className="shrink-0 p-1 rounded text-[#5c6175] hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {!inlineMode && (
        <button
          type="button"
          onClick={closeOverlay}
          className="shrink-0 text-[10px] text-[#5c6175] bg-primary-subtle border border-[#2d3142] rounded px-1.5 py-0.5 font-mono hover:text-white transition-colors"
        >
          ESC
        </button>
      )}
    </div>
  );

  const renderResultsPanel = () => (
    <div className={`overflow-y-auto flex-1 min-h-0 ${inlineMode ? 'max-h-[50vh] custom-scrollbar py-2' : ''}`}>
      {/* ── Recently Viewed ── */}
      {showRecent && (
        <div>
          <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-[#5c6175]" />
            <span className="text-[11px] font-semibold text-[#5c6175] uppercase tracking-wider">
              Recently Viewed
            </span>
          </div>
          {isRecentFetching ? (
            <SkeletonRows count={4} />
          ) : recentItems.length === 0 ? (
            <p className="px-4 py-6 text-sm text-[#5c6175] text-center">
              No recently viewed items yet.
            </p>
          ) : (
            recentItems.map((item, i) => (
              <ResultRow
                key={item.workItemUuid}
                result={item}
                isHighlighted={highlightedIndex === i}
                onSelect={handleSelect}
                onMouseEnter={() => setHighlightedIndex(i)}
              />
            ))
          )}
        </div>
      )}

      {/* ── Search results ── */}
      {showResults && (
        <>
          {isSearching && debouncedQuery !== query ? null : isLoading && groups.length === 0 ? (
            <SkeletonRows count={5} />
          ) : hasNoResults ? (
            <div className="px-4 py-10 flex flex-col items-center gap-2">
              <Search className="w-8 h-8 text-[#2d3142]" />
              <p className="text-sm text-[#5c6175]">No results for "{debouncedQuery}"</p>
              <p className="text-xs text-[#3d4156]">
                Try a different keyword or work item key
              </p>
            </div>
          ) : (
            groups.map((group) => {
              const groupStartIndex = flatIndex;
              flatIndex += group.results.length;
              return (
                <div key={group.groupLabel}>
                  {/* Group header */}
                  <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
                    <GroupIcon label={group.groupLabel} />
                    <span className="text-[11px] font-semibold text-[#5c6175] uppercase tracking-wider">
                      {group.groupLabel}
                    </span>
                    <span className="ml-auto text-[10px] font-bold text-[#8e94a5] bg-[#2d3142] px-1.5 py-0.5 rounded flex items-center justify-center min-w-[20px] tabular-nums">
                      {group.totalCount}
                    </span>
                  </div>

                  {/* Rows */}
                  {group.results.map((result, i) => (
                    <ResultRow
                      key={result.workItemUuid}
                      result={result}
                      isHighlighted={highlightedIndex === groupStartIndex + i}
                      onSelect={handleSelect}
                      onMouseEnter={() => setHighlightedIndex(groupStartIndex + i)}
                    />
                  ))}
                </div>
              );
            })
          )}
        </>
      )}

      {/* ── Footer hint ── */}
      {(showRecent && recentItems.length > 0) || (showResults && !hasNoResults && groups.length > 0) ? (
        <div className="px-4 py-2.5 border-t border-[#2d3142]/50 flex items-center justify-between text-[11px] text-[#3d4156] mt-2">
          <span className="flex items-center gap-3">
            <span>↑↓ navigate</span>
            <span>↵ open</span>
            <span>esc close</span>
          </span>
          <span className="flex items-center gap-1">
            Powered by <span className="text-[#8b5cf6] font-semibold">Worklap</span>
          </span>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {inlineMode ? (
        <div className={`relative ${className || ''}`} ref={overlayRef}>
          <div className="flex items-center w-full h-full bg-primary-variant border border-primary-subtle rounded-lg focus-within:border-blue-500 transition-colors">
            {renderInputRow()}
          </div>
          {isOpen && (
            <div className="absolute top-[calc(100%+8px)] left-0 w-full min-w-[250px] sm:min-w-[376px] z-[9999] bg-[#1c2030] border border-[#2d3142]/70 shadow-2xl rounded-xl overflow-hidden flex flex-col">
              {renderResultsPanel()}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* ── Trigger button ── */}
          <button
            type="button"
            onClick={openOverlay}
            className={`hidden sm:flex items-center gap-2 h-[38px] px-3 pr-10 rounded-lg bg-primary-variant border border-primary-subtle text-sm text-[#5c6175] hover:border-[#8b5cf6]/40 hover:text-[#8e94a5] transition-all duration-200 min-w-[200px] ${className || ''}`}
            aria-label="Open global search"
          >
            <Search className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{placeholder}</span>
            {!disableKeyboardShortcut && (
              <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] text-[#5c6175] bg-primary-subtle border border-[#2d3142] rounded px-1.5 py-0.5 font-mono">
                ⌘K
              </kbd>
            )}
          </button>

          {/* ── Fullscreen overlay ── */}
          {isOpen && (
            <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <div
                ref={overlayRef}
                className="relative w-full max-w-2xl rounded-2xl bg-[#1c2030] border border-[#2d3142]/70 shadow-2xl overflow-hidden flex flex-col"
                style={{ maxHeight: '70vh' }}
              >
                {renderInputRow()}
                {renderResultsPanel()}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Helper sub-components
// ---------------------------------------------------------------------------

const GroupIcon: React.FC<{ label: string }> = ({ label }) => {
  if (label.includes('Key')) return <Hash className="w-3.5 h-3.5 text-[#8b5cf6]" />;
  if (label.includes('Title')) return <ChevronRight className="w-3.5 h-3.5 text-blue-400" />;
  if (label.includes('Description')) return <FileText className="w-3.5 h-3.5 text-emerald-400" />;
  if (label.includes('Comment')) return <MessageSquare className="w-3.5 h-3.5 text-yellow-400" />;
  return <Search className="w-3.5 h-3.5 text-[#5c6175]" />;
};

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <div className="px-4 py-2 space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex flex-col gap-1.5 animate-pulse">
        <div className="flex items-center gap-2">
          <div className="h-3 w-14 bg-[#2d3142] rounded" />
          <div className="h-3 flex-1 bg-[#2d3142] rounded" />
        </div>
        <div className="h-2.5 w-3/4 bg-[#252838] rounded" />
      </div>
    ))}
  </div>
);

export default GlobalSearchBar;
